// @angular
import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
// Authorization services
import { AuthGuard } from "./services/shared/AuthGuardService.service";
import { RoleGuard } from "./services/shared/RoleGuardService.service";
import { CanDeactivateGuard } from "./services/shared/CanDeactivateGuardService.service";
import { PROFILES } from "../assets/profiles/profiles";
// Components
import { HomeComponent } from "./screens/dashboard/home/home.component";
import { HomeGlobalComponent } from "./screens/dashboard/home-global/home-global.component";
import { InstallationActivationComponent } from "./screens/dashboard/installation/installation-activation/installation-activation.component";
import { InstallationHistoricalComponent } from "./screens/dashboard/installation/installation-historical/installation-historical.component";
import { SubscriberMainComponent } from "./modules/subscriber-module/subscriber-main/subscriber-main.component";
import { SubscriberEditComponent } from "./modules/subscriber-module/subscriber-edit/subscriber-edit.component";
import { DemoGuard } from "./services/shared/DemoGuardService.service";
import { MainRedirectGuard } from "./services/shared/MainRedirectGuard.service";
import { HomeEntitiesComponent } from "./screens/dashboard/home-entities/home-entities.component";
import { ManagementCommStateComponent } from "./screens/dashboard/management/management-comm-state/management-comm-state.component";

/****************************************************************************************************/
// Routes
/****************************************************************************************************/

const routes: Routes = [
  // Subscriber
  {
    path: "abonado",
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRole: [PROFILES.ABONADO]
    },
    children: [
      {
        path: "principal",
        component: SubscriberMainComponent,
        canActivate: [RoleGuard],
        data: {
          expectedRole: [PROFILES.ARSON, PROFILES.ABONADO]
        }
      },
      {
        path: "configuracion",
        component: SubscriberEditComponent,
        canActivate: [RoleGuard],
        data: {
          expectedRole: [PROFILES.ARSON, PROFILES.ABONADO]
        }
      }
    ]
  },
  // Grouping (agrupacion)
  {
    path: "agrupacion",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION
      ]
    },
    loadChildren: () =>
      import("./routes/agrupation-routes/agrupation-routes.routes").then(m => m.agrupationRoutes)
  },
  // Groupings (agrupaciones)
  {
    path: "agrupaciones",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION
      ]
    },
    loadChildren: () =>
      import("./routes/agrupations-routes/agrupations-routes.routes").then(m => m.agrupationsRoutes)
  },
  // Alarms
  {
    path: "alarmas",
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
    loadChildren: () =>
      import("./routes/alarmas-routes/alarmas-routes.routes").then(m => m.alarmasRoutes)
  },
  // Data analysis
  {
    path: "analisis-datos",
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
    canActivateChild: [RoleGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION,
        PROFILES.USUARIO_TELEMEDIDA
      ]
    },
    loadChildren: () =>
      import("./routes/analisis-datos-routes/analisis-datos-routes.routes").then(
        m => m.analisisDatosRoutes
      )
  },
  // Clients
  {
    path: "clientes",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [PROFILES.ARSON, PROFILES.USUARIO_ARSON]
    },
    loadChildren: () =>
      import("./routes/clientes-routes/clientes-routes.routes").then(m => m.clientesRoutes)
  },
  // Coverage
  {
    path: "cobertura",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.ADMIN_CLIENTE
      ]
    },
    loadChildren: () =>
      import("./routes/cobertura-routes/cobertura-routes.routes").then(m => m.coberturaRoutes)
  },
  // CUPS
  {
    path: "cups",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION
      ]
    },
    loadChildren: () => import("./routes/cups-routes/cups-routes.routes").then(m => m.cupsRoutes)
  },
  // Devices (dispositivos)
  {
    path: "dispositivos",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION,
        PROFILES.ADMIN_INSTALLATION,
        PROFILES.TELEMEDIDA,
        PROFILES.USUARIO_TELEMEDIDA
      ]
    },
    loadChildren: () =>
      import("./routes/dispositivos-routes/dispositivos-routes.routes").then(
        m => m.dispositivosRoutes
      )
  },
  // Entities
  {
    path: "entidades",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.ADMIN_ENTIDAD
      ]
    },
    loadChildren: () =>
      import("./routes/entidades-routes/entidades-routes.routes").then(m => m.entidadesRoutes)
  },
  // Network status
  {
    path: "estado-red",
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
    loadChildren: () =>
      import("./routes/estado-red-routes/estado-red-routes.routes").then(m => m.estadoRedRoutes)
  },
  // Files
  {
    path: "ficheros",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION
      ]
    },
    loadChildren: () =>
      import("./routes/ficheros-routes/ficheros-routes.routes").then(m => m.ficherosRoutes)
  },
  // Sensors
  {
    path: "sensorica",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION,
        PROFILES.USUARIO_TELEMEDIDA
      ]
    },
    loadChildren: () =>
      import("./routes/sensorica-routes/sensorica-routes.routes").then(m => m.sensoricaRoutes)
  },
  // Gateways
  {
    path: "gateways",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [PROFILES.ARSON, PROFILES.USUARIO_ARSON]
    },
    loadChildren: () =>
      import("./routes/gateways-routes/gateways-routes.routes").then(m => m.gatewaysRoutes)
  },
  // Data management
  {
    path: "gestion-datos",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.USUARIO_AGRUPACION,
        PROFILES.USUARIO_TELEMEDIDA
      ]
    },
    loadChildren: () =>
      import("./routes/gestion-datos-routes/gestion-datos-routes.routes").then(
        m => m.gestionDatosRoutes
      )
  },
  // Work orders
  {
    path: "ordenes",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION,
        PROFILES.USUARIO_TELEMEDIDA,
        PROFILES.ADMIN_INSTALLATION
      ]
    },
    loadChildren: () =>
      import("./routes/ordenes-routes/ordenes-routes.routes").then(m => m.ordenesRoutes)
  },
  // Home
  {
    path: "principal",
    canActivate: [MainRedirectGuard],
    canDeactivate: [CanDeactivateGuard],
    children: [] // Required so canActivate runs in some versions when there is no component
  },
  // Home agrupacion
  {
    path: "principal-agrupacion",
    component: HomeComponent,
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION,
        PROFILES.USUARIO_TELEMEDIDA
      ]
    }
  },
  // Home entities
  {
    path: "principal-entidades",
    component: HomeEntitiesComponent,
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
      ]
    }
  },
  // Home global
  {
    path: "principal-global",
    component: HomeGlobalComponent,
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.ADMIN_ENTIDAD
      ]
    }
  },

  // Maintenance
  {
    path: "mantenimiento",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION
      ]
    },
    loadChildren: () =>
      import("./routes/mantenimiento-routes/mantenimiento-routes.routes").then(
        m => m.mantenimientoRoutes
      )
  },
  // Telecontrol
  {
    path: "telecontrol",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION,
        PROFILES.TELEMEDIDA,
        PROFILES.USUARIO_TELEMEDIDA
      ]
    },
    loadChildren: () =>
      import("./routes/telecontrol-routes/telecontrol-routes.routes").then(m => m.telecontrolRoutes)
  },
  // Users
  {
    path: "usuarios",
    canActivate: [AuthGuard, RoleGuard, DemoGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [
        PROFILES.ARSON,
        PROFILES.USUARIO_ARSON,
        PROFILES.ADMIN_CLIENTE,
        PROFILES.USUARIO_CLIENTE,
        PROFILES.ADMIN_ENTIDAD,
        PROFILES.USUARIO_ENTIDAD,
        PROFILES.ADMIN_AGRUPACION,
        PROFILES.USUARIO_AGRUPACION,
        PROFILES.ADMIN_INSTALLATION,
        PROFILES.INSTALADOR,
        PROFILES.TELEMEDIDA
      ]
    },
    loadChildren: () =>
      import("./routes/usuario-routes/usuario-routes.routes").then(m => m.usuarioRoutes)
  },
  // Installers
  {
    path: "instalador",
    canActivate: [AuthGuard, RoleGuard],
    canDeactivate: [CanDeactivateGuard],
    data: {
      expectedRole: [PROFILES.ARSON, PROFILES.USUARIO_ARSON, PROFILES.INSTALADOR]
    },
    children: [
      {
        path: "activacion",
        component: InstallationActivationComponent,
        canActivate: [RoleGuard],
        canDeactivate: [CanDeactivateGuard],
        data: {
          expectedRole: [PROFILES.ARSON, PROFILES.USUARIO_ARSON, PROFILES.INSTALADOR]
        }
      },
      {
        path: "historico",
        component: InstallationHistoricalComponent,
        canActivate: [RoleGuard],
        canDeactivate: [CanDeactivateGuard],
        data: {
          expectedRole: [PROFILES.ARSON, PROFILES.USUARIO_ARSON, PROFILES.INSTALADOR]
        }
      }
    ]
  },
  // Special
  {
    path: "acceso-denegado",
    loadComponent: () =>
      import("./screens/errors/401/401.component").then(m => m.AccesDeniedComponent)
  },
  {
    path: "**",
    loadComponent: () => import("./screens/errors/404/404.component").then(m => m.NotFoundComponent)
  }
];

/****************************************************************************************************/
// Exports
/****************************************************************************************************/

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: "enabled",
      onSameUrlNavigation: "reload",
      useHash: true
    })
  ],
  exports: [RouterModule]
})
export class RoutesModule { }
